<script setup>
import { consentSettingsShow } from '@/lib/consent'
</script>

<template>
  <div class="prose prose-lg max-w-none">
    <p class="text-gray-600 mb-6">Last updated: 04.11.2026</p>

    <p class="text-gray-700 mb-8">
      This Privacy Policy explains how Bindle Resources Ltd processes personal data relating to tutors.
    </p>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. Data Controller</h2>
      <p class="text-gray-700 mb-4">
        Bindle Resources Ltd is the data controller. Contact: privacy@bindle.co.uk
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Personal Data We Collect</h2>
      <p class="text-gray-700 mb-4">
        This may include identity data, contact data, ID documents, DBS details, payment information, platform usage data, communications, and Tutor Content metadata.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Data</h2>
      <p class="text-gray-700 mb-4">
        Data is used to onboard tutors, comply with safeguarding obligations, process payments, operate and improve the platform, and ensure platform security.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Legal Bases</h2>
      <p class="text-gray-700 mb-4">
        Processing is based on contract performance, legal obligations, legitimate interests, and consent where required.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing</h2>
      <p class="text-gray-700 mb-4">
        Data may be shared with payment processors, verification providers, regulators, and authorities as required by law. We do not sell personal data. If you give us your mobile number with a question, we may answer it on WhatsApp, which Meta operates.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
      <p class="text-gray-700 mb-4">
        Data is retained only as long as necessary for contractual and legal purposes.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
      <p class="text-gray-700 mb-4">
        Tutors may request access, correction, deletion, restriction, or portability of their data and may complain to the ICO.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Data Security</h2>
      <p class="text-gray-700 mb-4">
        Appropriate technical and organisational measures are used to protect data.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. International Transfers</h2>
      <p class="text-gray-700 mb-4">
        Where data is processed outside the UK, appropriate safeguards are applied.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Cookies and Analytics</h2>
      <p class="text-gray-700 mb-4">
        This section applies to everyone who visits Bindle. We count visits anonymously with PostHog, our analytics provider, to understand how Bindle is used and improve it: the pages visited, where a visit came from, the type of device and browser, and clicks and scrolling. This runs without asking first, as UK law allows for statistics used only to improve a service, and it stores nothing on your device and does not follow you from one visit to the next. PostHog processes it on our behalf, in the EU, and uses it for nothing else. You can turn it off at any time under "Cookie settings".
      </p>
      <p class="text-gray-700 mb-4">
        With your agreement, given by accepting in the cookie banner, we also use Google Analytics, Google Ads, the Meta Pixel and PostHog's recordings, to measure which of our adverts work. They set cookies, follow your visit from an advert to a booking, and tell Google and Meta which of our adverts brought you; PostHog records how pages are used, with anything typed into a form hidden. When you have agreed, our server also tells Google Analytics, Google Ads and Meta about the steps you take, such as signing up, writing to a tutor or booking a lesson, with the tutor's name and, for a booking, its amount. Google Analytics receives them under its own code for your browser, never your name or contact details. Google Ads receives only the code of the advert you clicked. Meta receives your email address, phone number and account number scrambled so they cannot be read, with your IP address, your browser and the codes Meta gave your browser and the advert, so it can match the step to the advert. Google and Meta use these results to learn which adverts and searches bring families to Bindle; we build no lists of people to show adverts to, and Google is told not to use them to personalise adverts. None of this happens if you reject, and we then store only your choice, so we do not ask again on every page.
      </p>
      <p class="text-gray-700 mb-4">
        When you arrive from one of our adverts, our server notes the advert's code and campaign labels for the length of your visit, whatever your choice, without storing anything on your device. It uses them only once you accept, on that page or a later one, so the advert that brought you is credited; if you never accept, they go with your visit. If you have an account and accepted, we keep the advert's code on your account for 90 days, the time Google and Meta credit an advert for, and then delete it; withdrawing your agreement deletes it straight away.
      </p>
      <p class="text-gray-700 mb-4">
        Separately, if you have an account, our server tells PostHog about the main steps of your use of Bindle: signing up, signing in, confirming your email address, looking at tutors, requesting and starting lessons with a tutor, lessons taking place and being paid for, and an arrangement ending. These records carry your account number, amounts, outcomes and the tutor's name, never your name or contact details, and set nothing on your device. We do this in our legitimate interest in understanding where the service works and where people give up, so we can improve it. You can object to it by writing to us. When an account is deleted, its records are deleted from PostHog too, within about a week.
      </p>
      <p class="text-gray-700 mb-4">
        You can change your choice at any time from "Cookie settings" in the site's footer, or here.
        <button type="button" class="font-medium text-brand underline" @click="consentSettingsShow">Change cookie settings</button>
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Updates</h2>
      <p class="text-gray-700 mb-4">
        This policy may be updated from time to time.
      </p>
    </section>
  </div>
</template>
